import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Shield, 
  Settings as SettingsIcon, 
  Save,
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  UserPlus,
  ShieldCheck,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  UserCheck,
  Cpu,
  Bell,
  Lock,
  Unlock,
  HelpCircle,
  BookOpen
} from 'lucide-react';
import DeletedRecords from '../DeletedRecords/DeletedRecords';
import SmartNotificationControl from './SmartNotificationControl';

const Settings = () => {
  const { t } = useTranslation();
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Check if user has access to settings
  useEffect(() => {
    if (user) {
      let hasAccess = false;
      
      // For admin users, check userType and role
      if (user.userType === 'admin') {
        hasAccess = user.role === 'Admin' || user.role === 'Manager' || user.isAdmin;
      } else {
        // For regular users, check roles array
        hasAccess = user.roles && user.roles.some(role => 
          role.RoleName === 'Manager' || role.RoleName === 'Admin'
        );
      }
      
      if (!hasAccess) {
        navigate('/dashboard');
        return;
      }
    }
  }, [user, navigate]);

  // Users state
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleConfig, setRoleConfig] = useState({ roleHierarchy: {}, permissions: {} });
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedSalesperson, setSelectedSalesperson] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Customers state for assignment
  const [customers, setCustomers] = useState([]);
  const [assignedCustomers, setAssignedCustomers] = useState([]);



  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
      // Fetch role config first, then roles
      const loadData = async () => {
        const config = await fetchRoleConfig();
        // Fetch roles with the config
        await fetchRolesWithConfig(config || roleConfig);
      };
      loadData();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      
      // Debug logging to compare with Layout data
      console.log('🔍 SETTINGS_DEBUG: Fetched users from /api/settings/users:', data.users);
      console.log('🔍 SETTINGS_DEBUG: Current AuthContext user:', user);
      console.log('🔍 SETTINGS_DEBUG: Data source comparison:', {
        settingsData: data.users?.find(u => u.UserID === user?.userId),
        authContextData: user,
        timestamp: new Date().toISOString(),
        dataSource: 'Settings API'
      });
      
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRolesWithConfig = async (config) => {
    // Use provided config or fall back to state
    const effectiveConfig = config || roleConfig;
    
    console.log('🔍 fetchRolesWithConfig called with config:', effectiveConfig);
    
    // First, ensure we have roles from config
    let allRoles = [];
    
    if (effectiveConfig && effectiveConfig.roleHierarchy && Object.keys(effectiveConfig.roleHierarchy).length > 0) {
      // Start with all roles from config
      const configRoleNames = Object.keys(effectiveConfig.roleHierarchy);
      console.log('🔍 Roles from config:', configRoleNames);
      
      // Create role objects from config
      allRoles = configRoleNames.map(roleName => ({
        RoleID: null, // Will be set from database if exists
        RoleName: roleName,
        UserCount: 0
      }));
      
      // Try to fetch roles from database to get RoleIDs
      try {
        const backendUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${backendUrl}/api/settings/roles`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const dbRoles = data.roles || [];
          console.log('🔍 Roles from database:', dbRoles.map(r => r.RoleName));
          
          // Update RoleIDs for roles that exist in database
          allRoles = allRoles.map(configRole => {
            const dbRole = dbRoles.find(r => r.RoleName === configRole.RoleName);
            if (dbRole) {
              return {
                ...configRole,
                RoleID: dbRole.RoleID,
                UserCount: dbRole.UserCount || 0
              };
            }
            return configRole;
          });
        }
      } catch (err) {
        console.warn('Failed to fetch roles from database, using config only:', err);
        // Continue with config roles only
      }
      
      // Filter out SuperAdmin for tenant-level users (only show at system level)
      allRoles = allRoles.filter(role => role.RoleName !== 'SuperAdmin');
      
      // Sort roles by hierarchy (higher hierarchy first)
      allRoles = allRoles.sort((a, b) => {
        const hierarchyA = effectiveConfig.roleHierarchy[a.RoleName] || 0;
        const hierarchyB = effectiveConfig.roleHierarchy[b.RoleName] || 0;
        return hierarchyB - hierarchyA; // Higher hierarchy first
      });
      
      console.log('🔍 Final merged roles (SuperAdmin filtered):', allRoles.map(r => r.RoleName));
      setRoles(allRoles);
    } else {
      // Fallback: try to fetch from database only
      try {
        const backendUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${backendUrl}/api/settings/roles`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch roles');
        const data = await response.json();
        setRoles(data.roles || []);
      } catch (err) {
        setError(err.message);
        setRoles([]);
      }
    }
  };

  // Keep the old function name for backward compatibility
  const fetchRoles = () => fetchRolesWithConfig(roleConfig);

  const fetchRoleConfig = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/tenant-roles/roles/config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.config) {
          console.log('🔍 Role config loaded:', Object.keys(data.config.roleHierarchy || {}));
          setRoleConfig(data.config);
          return data.config;
        }
      }
      console.warn('Failed to load role config: response not ok');
      return null;
    } catch (err) {
      console.warn('Failed to fetch role configuration:', err);
      return null;
    }
  };

  const fetchCustomers = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/customers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch customers');
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchAssignedCustomers = async (salespersonId) => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/users/${salespersonId}/assignments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch assignments');
      const data = await response.json();
      setAssignedCustomers(data.assignments || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      // Convert role names to RoleIDs before sending
      const processedUserData = await processRoleIds(userData);
      
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(processedUserData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to create user');
      }
      
      setSuccess(t('settings.userCreated'));
      setShowUserForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  // Helper function to convert role names/IDs to RoleIDs
  const processRoleIds = async (userData) => {
    if (!userData.roleIds || !Array.isArray(userData.roleIds)) {
      return userData;
    }

    const processedRoleIds = [];
    
    for (const roleIdOrName of userData.roleIds) {
      // If it's already a number, use it directly
      if (typeof roleIdOrName === 'number') {
        processedRoleIds.push(roleIdOrName);
        continue;
      }
      
      // If it's a string (role name), find the RoleID
      if (typeof roleIdOrName === 'string') {
        // First, check if we have it in the roles state
        const role = roles.find(r => r.RoleName === roleIdOrName);
        if (role && role.RoleID) {
          processedRoleIds.push(role.RoleID);
          continue;
        }
        
        // If role doesn't exist in database, we need to create it first
        // Try to get/create the role from the database
        try {
          const backendUrl = process.env.REACT_APP_API_URL || '';
          const roleResponse = await fetch(`${backendUrl}/api/settings/roles`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (roleResponse.ok) {
            const roleData = await roleResponse.json();
            const dbRole = roleData.roles?.find(r => r.RoleName === roleIdOrName);
            if (dbRole && dbRole.RoleID) {
              processedRoleIds.push(dbRole.RoleID);
              continue;
            }
          }
          
          // Role doesn't exist - create it
          const createRoleResponse = await fetch(`${backendUrl}/api/settings/roles`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ roleName: roleIdOrName })
          });
          
          if (createRoleResponse.ok) {
            // After creating, fetch roles again to get the new RoleID
            const updatedRolesResponse = await fetch(`${backendUrl}/api/settings/roles`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            
            if (updatedRolesResponse.ok) {
              const updatedRolesData = await updatedRolesResponse.json();
              const newRole = updatedRolesData.roles?.find(r => r.RoleName === roleIdOrName);
              if (newRole && newRole.RoleID) {
                processedRoleIds.push(newRole.RoleID);
                // Refresh roles state
                fetchRoles();
                continue;
              }
            }
          }
          
          console.warn(`Could not create or find role: ${roleIdOrName}`);
        } catch (err) {
          console.error(`Error processing role ${roleIdOrName}:`, err);
        }
      }
    }
    
    return {
      ...userData,
      roleIds: processedRoleIds.filter(id => id != null && !isNaN(id)) // Remove any null/undefined/non-numeric values
    };
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      // Convert role names to RoleIDs before sending
      const processedUserData = await processRoleIds(userData);
      
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(processedUserData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to update user');
      }
      
      setSuccess(t('settings.userUpdated'));
      setShowUserForm(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(t('settings.confirmDeleteUser', { name: `${user.FirstName} ${user.LastName}` }))) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/users/${user.UserID}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle specific error codes
        if (errorData.code === 'USER_HAS_RELATED_RECORDS') {
          // Format the detailed error message
          let detailedMessage = errorData.error;
          
          if (errorData.relatedRecords && errorData.relatedRecords.length > 0) {
            const recordsSummary = errorData.relatedRecords
              .map(record => `  • ${record.message}`)
              .join('\n');
            
            detailedMessage = `Cannot delete user "${user.FirstName} ${user.LastName}" because they have related records:\n\n${recordsSummary}\n\n`;
            detailedMessage += `Total: ${errorData.relatedRecords.reduce((sum, r) => sum + r.count, 0)} related record(s)\n\n`;
            detailedMessage += errorData.suggestion || 'Please reassign or remove these records first, or deactivate the user instead.';
          }
          
          setError(detailedMessage);
        } else if (errorData.code === 'FOREIGN_KEY_CONSTRAINT') {
          setError(errorData.error + '\n\n' + (errorData.suggestion || ''));
        } else {
          setError(errorData.error || errorData.message || 'Failed to delete user');
        }
        return;
      }
      
      setSuccess(t('settings.userDeleted'));
      fetchUsers();
    } catch (err) {
      console.error('Delete user error:', err);
      setError(err.message || 'Failed to delete user');
    }
  };

  const handleLockUser = async (user) => {
    const isLocked = user.IsActive; // If currently active, we want to lock (set isLocked=true)
    const action = isLocked ? 'lock' : 'unlock';
    const confirmMessage = isLocked
      ? `Are you sure you want to lock "${user.FirstName} ${user.LastName}"? They will not be able to log in, but all their data will remain intact.`
      : `Are you sure you want to unlock "${user.FirstName} ${user.LastName}"? They will be able to log in again.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/users/${user.UserID}/lock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isLocked: isLocked })
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || errorData.message || `Failed to ${action} user`);
        return;
      }

      const data = await response.json();
      setSuccess(data.detail || data.message || `User ${action}ed successfully`);
      fetchUsers();
    } catch (err) {
      console.error(`${action} user error:`, err);
      setError(err.message || `Failed to ${action} user`);
    }
  };

  const handleUserRoleUpdate = async (userId, newRoleId) => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ roleId: newRoleId })
      });

      if (!response.ok) throw new Error('Failed to update user role');
      
      setSuccess(t('settings.userRoleUpdated'));
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUserStatusToggle = async (user) => {
    const action = user.IsActive ? 'deactivate' : 'activate';
    const confirmMessage = user.IsActive 
      ? t('settings.confirmDeactivateUser', { name: `${user.FirstName} ${user.LastName}` })
      : t('settings.confirmActivateUser', { name: `${user.FirstName} ${user.LastName}` });

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/users/${user.UserID}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isActive: !user.IsActive })
      });

      if (!response.ok) throw new Error(`Failed to ${action} user`);
      
      setSuccess(t(`settings.user${action.charAt(0).toUpperCase() + action.slice(1)}d`));
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAssignCustomers = async (salespersonId, customerIds) => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/settings/users/${salespersonId}/assignments`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ customerIds })
      });

      if (!response.ok) throw new Error('Failed to assign customers');
      
      setSuccess(t('settings.customersAssigned'));
      setShowAssignmentModal(false);
      setSelectedSalesperson(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.Username?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                         user.Email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                         user.FirstName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                         user.LastName?.toLowerCase().includes(userSearchTerm.toLowerCase());
    
    const matchesRole = !selectedRole || user.roles?.some(role => role.RoleID === parseInt(selectedRole));
    const matchesStatus = selectedStatus === '' || 
                         (selectedStatus === 'active' && user.IsActive) ||
                         (selectedStatus === 'inactive' && !user.IsActive);
    
    return matchesSearch && matchesRole && matchesStatus;
  });



  const tabs = [
    { id: 'users', label: t('settings.users'), icon: Users },
    { id: 'roles', label: t('settings.roles'), icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'deleted-records', label: t('settings.deletedRecords'), icon: Trash2 }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <div className="flex items-center space-x-2">
          <button
            onClick={async () => {
              console.log('🔍 SETTINGS_DEBUG: Manual profile refresh requested');
              try {
                await refreshProfile();
                setSuccess('Profile refreshed successfully');
                setTimeout(() => setSuccess(null), 3000);
              } catch (error) {
                setError('Failed to refresh profile');
                setTimeout(() => setError(null), 3000);
              }
            }}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            title="Refresh user profile data"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh Profile</span>
          </button>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            <span>{t('common.refresh')}</span>
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
          <div>
            <p className="text-sm text-green-700">{success}</p>
          </div>
          <button
            onClick={() => setSuccess(null)}
            className="ml-auto text-green-400 hover:text-green-600"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-700 whitespace-pre-line">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600 flex-shrink-0"
          >
            ×
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow">
        {activeTab === 'users' && (
          <UserManagement
            users={filteredUsers}
            roles={roles}
            roleConfig={roleConfig}
            loading={loading}
            userSearchTerm={userSearchTerm}
            setUserSearchTerm={setUserSearchTerm}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            onUserRoleUpdate={handleUserRoleUpdate}
            onUserStatusToggle={handleUserStatusToggle}
            onCreateUser={handleCreateUser}
            onUpdateUser={handleUpdateUser}
            onDeleteUser={handleDeleteUser}
            onLockUser={handleLockUser}
            onAssignCustomers={handleAssignCustomers}
            showUserForm={showUserForm}
            setShowUserForm={setShowUserForm}
            editingUser={editingUser}
            setEditingUser={setEditingUser}
            showAssignmentModal={showAssignmentModal}
            setShowAssignmentModal={setShowAssignmentModal}
            selectedSalesperson={selectedSalesperson}
            setSelectedSalesperson={setSelectedSalesperson}
            customers={customers}
            assignedCustomers={assignedCustomers}
            fetchCustomers={fetchCustomers}
            fetchAssignedCustomers={fetchAssignedCustomers}
            t={t}
          />
        )}

        {activeTab === 'roles' && (
          <RoleManagement 
            roles={roles}
            loading={loading}
            onRefresh={fetchRoles}
            t={t}
          />
        )}
        {activeTab === 'notifications' && (
          <SmartNotificationControl
            t={t}
          />
        )}
        {activeTab === 'deleted-records' && (
          <div className="p-6">
            <DeletedRecords />
          </div>
        )}
      </div>
    </div>
  );
};

// User Management Component
const UserManagement = ({
  users, 
  roles, 
  roleConfig = { roleHierarchy: {}, permissions: {} },
  loading, 
  userSearchTerm, 
  setUserSearchTerm, 
  selectedRole, 
  setSelectedRole, 
  selectedStatus, 
  setSelectedStatus, 
  onUserRoleUpdate, 
  onUserStatusToggle, 
  onCreateUser, 
  onUpdateUser, 
  onDeleteUser, 
  onLockUser,
  onAssignCustomers, 
  showUserForm, 
  setShowUserForm, 
  editingUser, 
  setEditingUser, 
  showAssignmentModal, 
  setShowAssignmentModal, 
  selectedSalesperson, 
  setSelectedSalesperson, 
  customers, 
  assignedCustomers, 
  fetchCustomers, 
  fetchAssignedCustomers, 
  t 
}) => {
  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleAssignCustomers = async (user) => {
    setSelectedSalesperson(user);
    await fetchCustomers();
    await fetchAssignedCustomers(user.UserID);
    setShowAssignmentModal(true);
  };

  const isSalesperson = (user) => {
    return user.roles?.some(role => role.RoleName === 'Salesperson');
  };

  const isManagerOrAdmin = (user) => {
    return user.roles?.some(role => ['Manager', 'Admin'].includes(role.RoleName));
  };

  const [showHelpModal, setShowHelpModal] = useState(false);


  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h2 className="text-lg font-medium text-gray-900">{t('settings.userManagement')}</h2>
          <button
            onClick={() => setShowHelpModal(true)}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            title="View Roles and Policies Help"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
        </div>
        <button 
          onClick={handleCreateUser}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <UserPlus className="h-4 w-4" />
          <span>{t('settings.addUser')}</span>
        </button>
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <BookOpen className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-semibold text-gray-900">Roles and Data Governance Policies</h3>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <iframe
                src="/roles-and-policies-help.html"
                className="w-full h-full min-h-[600px] border-0"
                title="Roles and Policies Documentation"
              />
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowHelpModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex space-x-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('settings.searchUsers')}
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="pl-10 pr-8 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('settings.allRoles')}</option>
            {roles.map(role => (
              <option key={role.RoleID} value={role.RoleID}>{role.RoleName}</option>
            ))}
          </select>
        </div>
        <div className="relative">
          <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="pl-10 pr-8 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('settings.allStatuses')}</option>
            <option value="active">{t('settings.active')}</option>
            <option value="inactive">{t('settings.inactive')}</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('settings.user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('settings.email')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('settings.phone')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('settings.roles')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('settings.assignments')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('settings.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('settings.created')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('settings.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.UserID} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {user.FirstName?.[0]}{user.LastName?.[0]}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.FirstName} {user.LastName}
                        </div>
                        <div className="text-sm text-gray-500">@{user.Username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{user.Email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{user.Phone || 'N/A'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {user.roles?.map(role => (
                        <span
                          key={role.RoleID}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            role.RoleName === 'Admin' ? 'bg-red-100 text-red-800' :
                            role.RoleName === 'Manager' ? 'bg-purple-100 text-purple-800' :
                            'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {role.RoleName}
                        </span>
                      )) || (
                        <span className="text-sm text-gray-500">{t('settings.noRoles')}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isSalesperson(user) && (
                      <div className="flex items-center">
                        <UserCheck className="h-4 w-4 text-green-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {user.assignedCustomers || 0} {t('settings.customers')}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.IsActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.IsActive ? t('settings.active') : t('settings.inactive')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-500">
                        {new Date(user.CreatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditUser(user)}
                        className="text-blue-600 hover:text-blue-900"
                        title={t('common.edit')}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      {isSalesperson(user) && (
                        <button 
                          onClick={() => handleAssignCustomers(user)}
                          className="text-green-600 hover:text-green-900"
                          title={t('settings.assignCustomers')}
                        >
                          <Users className="h-4 w-4" />
                        </button>
                      )}
                      <button 
                        onClick={() => onLockUser(user)}
                        className={`${user.IsActive ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
                        title={user.IsActive ? 'Lock user (prevent login)' : 'Unlock user (allow login)'}
                      >
                        {user.IsActive ? (
                          <Lock className="h-4 w-4" />
                        ) : (
                          <Unlock className="h-4 w-4" />
                        )}
                      </button>
                      <button 
                        onClick={() => onDeleteUser(user)}
                        className="text-red-600 hover:text-red-900"
                        title={t('common.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User Form Modal */}
      {showUserForm && (
        <UserFormModal
          user={editingUser}
          roles={roles}
          roleConfig={roleConfig}
          onSubmit={editingUser ? 
            (userData) => onUpdateUser(editingUser.UserID, userData) : 
            onCreateUser
          }
          onClose={() => {
            setShowUserForm(false);
            setEditingUser(null);
          }}
          t={t}
        />
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && selectedSalesperson && (
        <AssignmentModal
          salesperson={selectedSalesperson}
          customers={customers}
          assignedCustomers={assignedCustomers}
          onSubmit={(customerIds) => onAssignCustomers(selectedSalesperson.UserID, customerIds)}
          onClose={() => {
            setShowAssignmentModal(false);
            setSelectedSalesperson(null);
          }}
          t={t}
        />
      )}
    </div>
  );
};

// Role Management Component
const RoleManagement = ({ roles, loading, onRefresh, t }) => {
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-gray-900">{t('settings.roleManagement')}</h2>
        <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          <Plus className="h-4 w-4" />
          <span>{t('settings.addRole')}</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map(role => (
            <div key={role.RoleID} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                  <h3 className="text-sm font-medium text-gray-900">{role.RoleName}</h3>
                </div>
                <div className="flex space-x-1">
                  <button className="text-blue-600 hover:text-blue-900">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button className="text-red-600 hover:text-red-900">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500">{role.Description || t('settings.noDescription')}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// User Form Modal Component
const UserFormModal = ({ user, roles, roleConfig = { roleHierarchy: {}, permissions: {} }, onSubmit, onClose, t }) => {
  const [formData, setFormData] = useState({
    firstName: user?.FirstName || '',
    lastName: user?.LastName || '',
    username: user?.Username || '',
    email: user?.Email || '',
    phone: user?.Phone || '',
    password: '',
    confirmPassword: '',
    roleIds: user?.roles?.map(role => role.RoleID || role.RoleName).filter(Boolean) || []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = t('settings.validation.firstNameRequired');
    if (!formData.lastName.trim()) newErrors.lastName = t('settings.validation.lastNameRequired');
    if (!formData.username.trim()) newErrors.username = t('settings.validation.usernameRequired');
    if (!formData.email.trim()) newErrors.email = t('settings.validation.emailRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t('settings.validation.emailInvalid');
    
    if (!user) { // Creating new user
      if (!formData.password) newErrors.password = t('settings.validation.passwordRequired');
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = t('settings.validation.passwordMismatch');
    }
    
    if (formData.roleIds.length === 0) newErrors.roles = t('settings.validation.roleRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Form submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleRoleChange = (roleId, checked) => {
    setFormData(prev => ({
      ...prev,
      roleIds: checked 
        ? [...prev.roleIds, roleId]
        : prev.roleIds.filter(id => id !== roleId)
    }));
    if (errors.roles) {
      setErrors(prev => ({ ...prev, roles: null }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {user ? t('settings.editUser') : t('settings.createUser')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.firstName')} *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleFieldChange('firstName', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.lastName')} *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleFieldChange('lastName', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.username')} *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleFieldChange('username', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.username ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.email')} *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.phone')}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Password Fields (only for new users) */}
          {!user && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.password')} *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.confirmPassword')} *
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>
            </div>
          )}

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('settings.roles')} *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {roles && roles.length > 0 ? (
                roles.map((role, index) => {
                  // Use RoleID if available, otherwise use RoleName as key
                  const roleKey = role.RoleID || role.RoleName || `role-${index}`;
                  const hierarchy = roleConfig.roleHierarchy?.[role.RoleName];
                  const roleDisplayName = hierarchy !== undefined 
                    ? `${role.RoleName} (Level ${hierarchy})` 
                    : role.RoleName;
                  
                  // For roles without RoleID (from config), use RoleName as identifier
                  // Backend will need to handle creating the role if it doesn't exist
                  const roleIdToUse = role.RoleID || role.RoleName;
                  
                  // Check if this role is selected (handle both RoleID and RoleName)
                  const isChecked = formData.roleIds.some(id => 
                    id === roleIdToUse || 
                    (typeof id === 'number' && id === role.RoleID) ||
                    (typeof id === 'string' && id === role.RoleName)
                  );
                  
                  return (
                    <label key={roleKey} className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => handleRoleChange(roleIdToUse, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 font-medium">{roleDisplayName}</span>
                      {!role.RoleID && (
                        <span className="text-xs text-gray-400 italic" title="This role will be created in the database when assigned">(new)</span>
                      )}
                    </label>
                  );
                })
              ) : (
                <div className="col-span-full text-sm text-gray-500 italic">
                  {loading ? 'Loading roles...' : 'No roles available. Please check role configuration.'}
                </div>
              )}
            </div>
            {errors.roles && <p className="mt-1 text-sm text-red-600">{errors.roles}</p>}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{loading ? t('common.saving') : t('common.save')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Assignment Modal Component
const AssignmentModal = ({ salesperson, customers, assignedCustomers, onSubmit, onClose, t }) => {
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(
    assignedCustomers.map(customer => customer.CustomerID) || []
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      customer.FirstName?.toLowerCase().includes(searchLower) ||
      customer.LastName?.toLowerCase().includes(searchLower) ||
      customer.CompanyName?.toLowerCase().includes(searchLower) ||
      customer.Email?.toLowerCase().includes(searchLower)
    );
  });

  const handleCustomerToggle = (customerId) => {
    setSelectedCustomerIds(prev => 
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(selectedCustomerIds);
    } catch (err) {
      console.error('Assignment error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {t('settings.assignCustomers')}
            </h3>
            <p className="text-sm text-gray-500">
              {t('settings.assignCustomersTo', { name: `${salesperson.FirstName} ${salesperson.LastName}` })}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('settings.searchCustomers')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Customer List */}
          <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
            {filteredCustomers.map(customer => (
              <label key={customer.CustomerID} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCustomerIds.includes(customer.CustomerID)}
                  onChange={() => handleCustomerToggle(customer.CustomerID)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">
                      {customer.CustomerType === 'Person' 
                        ? `${customer.FirstName} ${customer.LastName}`
                        : customer.CompanyName
                      }
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      customer.CustomerType === 'Person' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {customer.CustomerType}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {customer.Email && (
                      <span className="flex items-center space-x-1">
                        <Mail className="h-3 w-3" />
                        <span>{customer.Email}</span>
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              {t('settings.selectedCustomers', { count: selectedCustomerIds.length })}
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <UserCheck className="h-4 w-4" />
              )}
              <span>{loading ? t('common.saving') : t('settings.assignCustomers')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings; 